import { MutationCache, QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import * as React from "react";
import * as ReactDOM from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import { BrowserRouter } from "react-router-dom";
import { AxiosError } from "axios";
import { toast, Toaster } from "sonner";

import App from "./App";
// import { Toaster } from "./components/ui/sonner";
import { Provider } from "./provider";
import { apiClient } from "./api/api.ts";
import { TPhaseIISchema } from "./models/PhaseII.schema.ts";
import { TPhaseIIISchema } from "./models/PhaseIII.schema.ts";
import {
  registeringAnimal,
  updateAnimal,
} from "./features/Animals/animalApiSlice.ts";

registerSW({ immediate: true });

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
  throttleTime: 1000,
});

// Animal records are shared between admin and farmer sessions and must always
// come from the API. Do not restore old animal snapshots from local storage.
const persistenceBuster = "live-animal-data-v3";
const shouldPersistQuery = (query: any) => {
  const rootKey = String(query.queryKey?.[0] ?? "");
  return ![
    "animals",
    "animal",
    "SilaAnimal",
    "farmerOwnedAnimals",
    "allAnimals",   // admin snapshot — must never bleed into a new session
    "allFarmers",
    "allFarms",
    "allSupervisors",
    "allOfficials",
  ].includes(rootKey);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: Infinity, // 24 hours
      networkMode: "offlineFirst",
      staleTime: Infinity,
    },
  },
  mutationCache: new MutationCache({
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        toast.error(`${error.response?.data.message}`, {
          duration: 1000,
          position: "top-right",
        });
      }
    },
  }),
});

queryClient.setMutationDefaults(["statusChange"], {
  mutationFn: async (data: { id: string; status: string; url: string }) => {
    return await apiClient.post(data.url, data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: ["allSupervisors", "allFarmers"],
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["supervisor"], {
  mutationFn: async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role: string;
    phoneNumber: string;
    address: string;
    country: string;
    state: string;
    city: string;
    zipCode: string;
  }) => {
    return await apiClient.post("/admin/registerSupervisor/", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["allSupervisors"] });
    toast.success("New Supervisor Has Been Registered Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["animals"], {
  mutationFn: async (data: any) => {
    return await apiClient.post("/phaseI", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["animals"] });
    toast.success("Maternal Phase Updated Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["animals"], {
  mutationFn: async (data: TPhaseIISchema) => {
    return await apiClient.post("/phaseII", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["animals"] });
    toast.success("Weaning Phase Updated Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["animals"], {
  mutationFn: async (data: TPhaseIIISchema) => {
    return await apiClient.post("/phaseIII", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["animals"] });
    toast.success("Yearling Phase Updated Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["registerAnimals"], {
  mutationFn: async (data) => {
    return await registeringAnimal(data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["animals"] });
    toast.success("Animal Registered Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["updateAnimal"], {
  mutationFn: async (data) => {
    return await updateAnimal(data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["allAnimals"] });
    toast.success("Animal Detailed Updated Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["assignSupervisor"], {
  mutationFn: async (data) => {
    return await apiClient.patch("/admin/assignSupervisor", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["allFarms"] });
    queryClient.invalidateQueries({ queryKey: ["allSupervisors"] });
    toast.success("Supervisor assigned Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["setAnimalBreedEvaluation"], {
  mutationFn: async (data: { id: string; evaluation: string }) => {
    return await apiClient.patch("/animals/breedEvaluation", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["getSupervisorAnimal"] });
    toast.success("Yearling Phase Updated Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["setPermanentDGR"], {
  mutationFn: async (data: { id: string; evaluation: string }) => {
    return await apiClient.patch("/animals/dgrAssignation", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["getSupervisorAnimal"] });
    toast.success("Yearling Phase Updated Successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

queryClient.setMutationDefaults(["registerOfficials"], {
  mutationFn: async (data: { id: string; evaluation: string }) => {
    return await apiClient.post("/admin/registerOfficials", data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["allOfficials"] });
    toast.success("Government Official has been Registered successfully", {
      duration: 1000,
      position: "top-right",
    });
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      toast.error(`${error.response?.data.message}`);
    }
  },
});

if ("serviceWorker" in navigator) {
  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        await navigator.serviceWorker.register("/sw.js");
        // console.log("Service Worker registered successfully");
      } else {
        // console.log("Service Worker already registered");
      }
    } catch (error) {
      console.error("Service Worker registration failed:", error);
    }
  });
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: localStoragePersister,
        buster: persistenceBuster,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}
      onSuccess={() => {
        // resume mutations after initial restore from
        // localStorage was successful
        queryClient.resumePausedMutations().then(() => {
          queryClient.invalidateQueries();
        });
      }}
    >
      <BrowserRouter>
        <Provider>
          <App />
          <Toaster
            expand
            richColors
            position="top-right"
            theme="light"
            visibleToasts={2}
          />
          {/*<ReloadPrompt />*/}
        </Provider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
